/**
 * Utility for resizing images before upload
 */

/**
 * Resizes an image file to a maximum width/height while maintaining aspect ratio
 * @param {File} file - The image file to resize
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} maxHeight - Maximum height in pixels
 * @param {number} quality - JPEG quality (0-1), default 0.9
 * @returns {Promise<Blob>} - Promise resolving to the resized image blob
 */
export const resizeImage = (file, maxWidth = 200, maxHeight = 200, quality = 0.9) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;
        
        // Only resize if image is larger than max dimensions
        if (width > maxWidth || height > maxHeight) {
          // Calculate aspect ratio
          const aspectRatio = width / height;
          
          if (width > height) {
            // Landscape orientation
            width = maxWidth;
            height = width / aspectRatio;
            
            // Check if height exceeds max
            if (height > maxHeight) {
              height = maxHeight;
              width = height * aspectRatio;
            }
          } else {
            // Portrait or square orientation
            height = maxHeight;
            width = height * aspectRatio;
            
            // Check if width exceeds max
            if (width > maxWidth) {
              width = maxWidth;
              height = width / aspectRatio;
            }
          }
        }
        
        // Create canvas and resize
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        
        // Use better image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          file.type === 'image/png' ? 'image/png' : 'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Creates a File object from a Blob
 * @param {Blob} blob - The blob to convert
 * @param {string} filename - The filename for the new File
 * @returns {File} - The new File object
 */
export const blobToFile = (blob, filename) => {
  return new File([blob], filename, { type: blob.type });
};